import { useState, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { CheckCircle, XCircle, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

function normalizeTranslation(str) {
  return str.trim().toLowerCase().replace(/[.,;:!?]/g, '').replace(/\s+/g, ' ');
}

export default function TranslationExercise({
  phase,
  onPhaseComplete,
  checkAnswer,
  'data-testid': testId = 'translation-exercise',
}) {
  const { t } = useLanguage();
  const { play } = useSoundEffects();
  const { fireSmall } = useConfetti();
  const shouldReduce = useReducedMotion();

  const questions = phase?.content?.questions || [];
  const maxXP = phase?.xp_in_phase || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const textareaRef = useRef(null);
  // Server-side grading (Bug 3)
  const collectedAnswers = useRef([]);
  const [serverResult, setServerResult] = useState(null);
  // checking: true while awaiting server check-answer response (suppress color flash)
  const [checking, setChecking] = useState(false);

  const currentQ = questions[currentIndex];
  const isLast = currentIndex === questions.length - 1;

  // Derive all values with safe access
  const sourceLang = currentQ?.source_lang || 'tr';
  const targetLang = sourceLang === 'tr' ? 'en' : 'tr';

  const sourceText = sourceLang === 'tr'
    ? (currentQ?.source_tr || currentQ?.source || '')
    : (currentQ?.source_en || currentQ?.source_tr || currentQ?.source || '');

  const correctTranslation = targetLang === 'en'
    ? (currentQ?.correct_translation_en || currentQ?.accepted_answers_en?.[0] || '')
    : (currentQ?.correct_translation_tr || currentQ?.accepted_answers_tr?.[0] || '');

  // Stabilize acceptedAnswers: inline `|| [correctTranslation]` creates a new
  // array every render, causing isAnswerCorrect useCallback to re-run needlessly.
  const acceptedAnswers = useMemo(() =>
    targetLang === 'en'
      ? (currentQ?.accepted_answers_en || [correctTranslation])
      : (currentQ?.accepted_answers_tr || [correctTranslation]),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [currentIndex, targetLang, correctTranslation]);

  // All useCallbacks unconditionally (before early return)
  const isAnswerCorrect = useCallback((userAnswer) => {
    const norm = normalizeTranslation(userAnswer);
    return acceptedAnswers.some((ans) => normalizeTranslation(ans) === norm);
  }, [acceptedAnswers]);

  const handleSubmit = useCallback(async () => {
    if (!answer.trim() || submitted) return;
    setSubmitted(true);

    collectedAnswers.current = [
      ...collectedAnswers.current,
      { question_index: currentIndex, text: answer },
    ];

    let correct;
    if (typeof checkAnswer === 'function') {
      // Renk flash'ini onlemek icin server yaniti gelene kadar checking=true tut
      setChecking(true);
      const res = await checkAnswer({
        phaseId: phase?.phase_id,
        questionIndex: currentIndex,
        answer: { text: answer },
      });
      setChecking(false);
      if (res) {
        setServerResult(res);
        correct = !!res.is_correct;
      } else {
        correct = isAnswerCorrect(answer);
      }
    } else {
      correct = isAnswerCorrect(answer);
    }

    if (correct) {
      play('correct');
      if (!shouldReduce) fireSmall({ y: 0.5 });
      setCorrectCount((c) => c + 1);
    } else {
      play('wrong');
    }
  }, [answer, submitted, currentIndex, checkAnswer, phase, isAnswerCorrect, play, shouldReduce, fireSmall]);

  const handleNext = useCallback(() => {
    if (isLast) {
      const score = questions.length > 0
        ? Math.round((correctCount / questions.length) * maxXP)
        : 0;
      onPhaseComplete({
        score,
        maxScore: maxXP,
        answers: collectedAnswers.current, // [{question_index, text}]
        timeSpent: 0,
      });
    } else {
      setCurrentIndex((i) => i + 1);
      setAnswer('');
      setSubmitted(false);
      setServerResult(null);
      setChecking(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isLast, correctCount, questions.length, maxXP, onPhaseComplete]);

  // Early return after all hooks
  if (!currentQ) return null;

  // checking=true means we're awaiting server; don't apply color until server responds
  const correct = submitted && !checking && (serverResult ? !!serverResult.is_correct : isAnswerCorrect(answer));
  const wrong = submitted && !checking && !correct;
  const displayCorrectTranslation =
    serverResult && serverResult.correct_answer != null
      ? serverResult.correct_answer
      : correctTranslation;

  const sourceLangLabel = sourceLang === 'tr'
    ? (t('exercise.language_tr') || 'Türkçe')
    : (t('exercise.language_en') || 'İngilizce');

  const targetLangLabel = targetLang === 'en'
    ? (t('exercise.language_en') || 'İngilizce')
    : (t('exercise.language_tr') || 'Türkçe');

  return (
    <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto" data-testid={testId}>
      {/* Progress */}
      <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
        <span data-testid={`${testId}-progress`}>
          {(t('exercise.question_counter') || 'Soru {current} / {total}')
            .replace('{current}', currentIndex + 1)
            .replace('{total}', questions.length)}
        </span>
        {maxXP > 0 && (
          <span className="font-semibold text-amber-500">
            {(t('exercise.max_xp') || 'Maks. {xp} XP')
              .replace('{xp}', maxXP)}
          </span>
        )}
      </div>

      {/* Source sentence */}
      <motion.div
        key={currentIndex}
        initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="p-4 rounded-xl bg-gray-50 border-2 border-[var(--border-light)]"
        data-testid={`${testId}-source`}
      >
        <p className="text-xs text-[var(--text-muted)] mb-2 uppercase tracking-wide font-semibold">
          {sourceLangLabel}
        </p>
        <p className="text-lg font-bold text-[var(--text-main)]">
          {sourceText}
        </p>
      </motion.div>

      {/* Target input */}
      <div>
        <label
          htmlFor={`${testId}-textarea`}
          className="block text-xs text-[var(--text-muted)] uppercase tracking-wide font-semibold mb-2"
        >
          {t('exercise.translate_to') || `${targetLangLabel} çevirisi`}
        </label>
        <textarea
          id={`${testId}-textarea`}
          ref={textareaRef}
          value={answer}
          onChange={(e) => !submitted && setAnswer(e.target.value)}
          disabled={submitted}
          placeholder={t('exercise.translation_placeholder') || 'Çevirinizi yazın...'}
          rows={3}
          className="w-full px-4 py-3 border-2 border-[var(--border-light)] rounded-xl text-[var(--text-main)] focus:border-[var(--brand-primary)] focus:outline-none disabled:bg-gray-50 disabled:cursor-default transition-colors resize-none"
          aria-label={t('exercise.translation_input_label') || 'Çeviri girişi'}
          data-testid={`${testId}-input`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !submitted) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <p className="text-xs text-[var(--text-muted)] mt-1">
          {t('exercise.enter_to_submit') || 'Enter: gönder, Shift+Enter: yeni satır'}
        </p>
      </div>

      {/* Feedback — only shown after server responds (checking=false) */}
      <AnimatePresence>
        {submitted && !checking && (
          <motion.div
            key="feedback"
            initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 ${
                correct
                  ? 'border-[#10B981] bg-[#10B981]/10'
                  : 'border-[var(--brand-danger)] bg-[var(--brand-danger)]/10'
              }`}
              role="alert"
              data-testid={`${testId}-feedback`}
            >
              {correct ? (
                <CheckCircle size={20} className="text-[#10B981] shrink-0 mt-0.5" aria-hidden="true" />
              ) : (
                <XCircle size={20} className="text-[var(--brand-danger)] shrink-0 mt-0.5" aria-hidden="true" />
              )}
              <div>
                <p className={`text-sm font-bold mb-1 ${correct ? 'text-emerald-700' : 'text-red-700'}`}>
                  {correct ? (t('exercise.correct') || 'Doğru!') : (t('exercise.wrong') || 'Yanlış!')}
                </p>
                {wrong && (
                  <p className="text-sm text-[var(--text-muted)]">
                    {t('exercise.correct_translation') || 'Doğru çeviri:'}{' '}
                    <strong>{displayCorrectTranslation}</strong>
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buttons */}
      <div className="flex justify-end">
        {!submitted ? (
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.99 }}
            onClick={handleSubmit}
            disabled={!answer.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            data-testid={`${testId}-submit`}
          >
            {t('exercise.submit') || 'Kontrol Et'}
          </motion.button>
        ) : checking ? (
          /* Awaiting server grading — neutral loading state */
          <div
            className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-400 font-bold rounded-xl cursor-wait"
            data-testid={`${testId}-checking`}
            aria-busy="true"
            aria-label={t('exercise.checking') || 'Kontrol ediliyor...'}
          >
            <span className="w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" aria-hidden="true" />
            {t('exercise.checking') || 'Kontrol ediliyor...'}
          </div>
        ) : (
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.01 }}
            whileTap={shouldReduce ? {} : { scale: 0.99 }}
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
            data-testid={`${testId}-next`}
          >
            {isLast ? (t('exercise.finish') || 'Bitir') : (t('exercise.next') || 'Sonraki')}
            <ChevronRight size={18} aria-hidden="true" />
          </motion.button>
        )}
      </div>
    </div>
  );
}
